/**
 * Style Engine — pure functions, no React or DB dependency.
 * Resolves block styles, tokens, and responsive overrides.
 */

import type {
  BlockState,
  BlockTypography,
  BlockColors,
  BlockVisualStyles,
  BlockBreakpointOverride,
  Device,
} from '../store/editor.store'
import type { ApiDesignToken } from '../utils/api'

// ── Token map ─────────────────────────────────────────────────────────────────

export type TokenMap = Record<string, string>

export function buildTokenMap(tokens: ApiDesignToken[]): TokenMap {
  const map: TokenMap = {}
  for (const t of tokens) {
    map[t.key] = t.value
  }
  // Second pass: resolve aliases
  for (const t of tokens) {
    if (t.aliasOf && map[t.aliasOf]) {
      map[t.key] = map[t.aliasOf]
    }
  }
  return map
}

export function buildCssVars(tokens: ApiDesignToken[]): Record<string, string> {
  const map = buildTokenMap(tokens)
  const vars: Record<string, string> = {}
  for (const [key, value] of Object.entries(map)) {
    // "color.primary" → "--ds-color-primary"
    const varName = `--ds-${key.replace(/\./g, '-')}`
    vars[varName] = value
  }
  return vars
}

// Resolve a single value: if it's a token reference ("color.primary"), look it up.
// Otherwise return the value as-is.
export function resolveToken(value: string | undefined, tokens: TokenMap): string | undefined {
  if (!value) return value
  if (value.startsWith('token:')) {
    const key = value.slice(6)
    return tokens[key] ?? value
  }
  return value
}

// ── Responsive resolution ─────────────────────────────────────────────────────

const BREAKPOINT_ORDER: Device[] = ['desktop', 'tablet', 'mobile']

export function resolveBreakpointOverride(
  block: BlockState,
  device: Device,
): BlockBreakpointOverride {
  const responsive = block.blockResponsiveStyles ?? {}
  const result: BlockBreakpointOverride = {}

  // Cascade: base (undefined) → desktop → tablet → mobile
  // Apply all overrides up to and including the current device
  const idx = BREAKPOINT_ORDER.indexOf(device)
  for (let i = 0; i <= idx; i++) {
    const bp = BREAKPOINT_ORDER[i]
    const override = (responsive as Record<string, BlockBreakpointOverride>)[bp]
    if (override) {
      Object.assign(result, override)
    }
  }

  return result
}

// ── Resolved style object ─────────────────────────────────────────────────────

export interface ResolvedBlockStyle {
  // Wrapper/container
  paddingTop?: number
  paddingBottom?: number
  paddingLeft?: number
  paddingRight?: number
  marginTop?: number
  marginBottom?: number
  marginLeft?: number
  marginRight?: number
  minHeight?: number
  background?: string

  // Typography
  fontFamily?: string
  fontWeight?: string
  textAlign?: string
  letterSpacing?: string
  lineHeight?: string
  headingSize?: string
  bodySize?: string

  // Colors
  color?: string       // textColor
  accentColor?: string

  // Visual effects
  borderRadius?: string
  borderWidth?: string
  borderStyle?: string
  borderColor?: string
  boxShadow?: string
  opacity?: number
  backdropFilter?: string
  mixBlendMode?: string
  rotate?: string
  scale?: string
  overflow?: string

  // Visibility
  hidden?: boolean
}

export function resolveBlockStyle(
  block: BlockState,
  tokens: TokenMap,
  device: Device,
): ResolvedBlockStyle {
  const override = resolveBreakpointOverride(block, device)
  const hidden = override.hideBlock === true

  const typo: BlockTypography = { ...block.blockTypography }
  const colors: BlockColors = { ...block.blockColors }
  const visual: BlockVisualStyles = { ...block.blockVisualStyles }

  // Responsive override merges into typo/wrapper
  if (override.headingSize) typo.headingSize = override.headingSize
  if (override.bodySize) typo.bodySize = override.bodySize

  const paddingTop = override.wrapPaddingTop ?? block.wrapPaddingTop
  const paddingBottom = override.wrapPaddingBottom ?? block.wrapPaddingBottom
  const paddingLeft = override.wrapPaddingLeft ?? block.wrapPaddingLeft
  const paddingRight = override.wrapPaddingRight ?? block.wrapPaddingRight
  const minHeight = override.blockMinHeight ?? block.blockMinHeight

  return {
    paddingTop,
    paddingBottom,
    paddingLeft,
    paddingRight,
    minHeight,
    background: resolveToken(block.wrapBackground, tokens),

    fontFamily: resolveToken(typo.fontFamily, tokens),
    fontWeight: typo.fontWeight,
    textAlign: typo.textAlign,
    letterSpacing: typo.letterSpacing,
    lineHeight: typo.lineHeight,
    headingSize: typo.headingSize,
    bodySize: typo.bodySize,

    color: resolveToken(colors.textColor, tokens),
    accentColor: resolveToken(colors.accentColor, tokens),

    borderRadius: resolveToken(visual.borderRadius, tokens),
    borderWidth: visual.borderWidth,
    borderStyle: visual.borderStyle,
    borderColor: resolveToken(visual.borderColor, tokens),
    boxShadow: resolveToken(visual.boxShadow, tokens),
    opacity: visual.opacity,
    backdropFilter: visual.backdropFilter,
    mixBlendMode: visual.mixBlendMode,
    rotate: visual.rotate,
    scale: visual.scale,
    overflow: visual.overflow,

    hidden,
  }
}

// ── CSS inline style object ───────────────────────────────────────────────────

export type CssStyleObject = Record<string, string | number | undefined>

export function resolvedStyleToCss(style: ResolvedBlockStyle): CssStyleObject {
  const css: CssStyleObject = {}
  if (style.paddingTop != null) css.paddingTop = style.paddingTop
  if (style.paddingBottom != null) css.paddingBottom = style.paddingBottom
  if (style.paddingLeft != null) css.paddingLeft = style.paddingLeft
  if (style.paddingRight != null) css.paddingRight = style.paddingRight
  if (style.minHeight != null) css.minHeight = style.minHeight
  if (style.background) css.background = style.background
  if (style.color) css.color = style.color
  if (style.fontFamily) css.fontFamily = style.fontFamily
  if (style.fontWeight) css.fontWeight = style.fontWeight
  if (style.textAlign) css.textAlign = style.textAlign
  if (style.letterSpacing) css.letterSpacing = style.letterSpacing
  if (style.lineHeight) css.lineHeight = style.lineHeight
  if (style.borderRadius) css.borderRadius = style.borderRadius
  if (style.borderWidth) css.borderWidth = style.borderWidth
  if (style.borderStyle) css.borderStyle = style.borderStyle
  if (style.borderColor) css.borderColor = style.borderColor
  if (style.boxShadow) css.boxShadow = style.boxShadow
  if (style.opacity != null) css.opacity = style.opacity
  if (style.backdropFilter) css.backdropFilter = style.backdropFilter
  if (style.rotate) css.transform = `rotate(${style.rotate})`
  if (style.overflow) css.overflow = style.overflow
  if (style.hidden) css.display = 'none'
  return css
}
